import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI, clubAPI } from '../services/api';
import './RegisterClub.css';

function RegisterClub() {
  const [searchParams, setSearchParams] = useSearchParams();
  const siteId = searchParams.get('_si');
  const currentStep = parseInt(searchParams.get('step') || '0');

  const [formData, setFormData] = useState({
    // Step 0 - Club Details
    name: '',

    // Step 1 - Betting Settings
    betValue: '10',
    betType: 'PERCENT',
    betFrequency: 'weekly',
    puntersPerPeriod: '1',
    rolloverDay: 'Sunday',

    // Step 2 - Member Contributions
    contributionAmount: '20',
    contributionFrequency: 'weekly',

    // Step 3 - Bookie
    bookieId: 'grsbet',
    accountAction: '', // 'new' or 'existing'
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  const steps = [
    { title: 'Club Details', description: 'Name your syndicate' },
    { title: 'Betting Settings', description: 'How will you bet?' },
    { title: 'Member Contributions', description: 'How do members chip in?' },
    { title: 'Connect Bookie', description: 'Link your betting account' },
  ];

  useEffect(() => {
    const loadUser = async () => {
      try {
        const userData = await authAPI.getCurrentUser();
        setUser(userData.user);
      } catch (err) {
        console.error('Not authenticated:', err);
        navigate('/login');
      }
    };

    loadUser();
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleNext = (e) => {
    e.preventDefault();
    setError('');

    // Validate current step
    if (currentStep === 0 && !formData.name) {
      setError('Club name is required');
      return;
    }

    // Validate Step 3 - Bookie account action
    if (currentStep === 3) {
      if (!formData.accountAction) {
        setError('Please choose whether you need a new account or already have one');
        return;
      }
    }

    if (currentStep < steps.length - 1) {
      const params = { step: (currentStep + 1).toString() };
      if (siteId) params._si = siteId;
      setSearchParams(params);
      window.scrollTo(0, 0);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      const params = { step: (currentStep - 1).toString() };
      if (siteId) params._si = siteId;
      setSearchParams(params);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    // Guard: name is required
    if (!formData.name || formData.name.length < 2) {
      setError('Please name your club (minimum 2 characters). Go back to step 1.');
      const params = { step: '0' };
      if (siteId) params._si = siteId;
      setSearchParams(params);
      window.scrollTo(0, 0);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await clubAPI.createClub(formData);

      if (response.success) {
        setSuccess('Club created successfully!');

        const clubId = response.club?.id;
        if (clubId) {
          // Redirect to bookie connection page
          setSuccess('Redirecting to bookie setup...');
          setTimeout(() => {
            navigate(`/register/link?clubId=${clubId}&action=new`);
          }, 1500);
        } else {
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000);
        }
      } else {
        setError(response.error || 'Failed to create club');
        setLoading(false);
      }
    } catch (err) {
      console.error('Club creation error:', err);
      const details = err.response?.data?.details;
      const baseErr = err.response?.data?.error || 'An error occurred. Please try again.';
      setError(details && details.length ? `${baseErr}: ${details.join(', ')}` : baseErr);
      setLoading(false);
    }
  };

  const handleSkip = () => {
    navigate('/dashboard');
  };

  if (!user) {
    return null;
  }

  return (
    <div className="register-club-wrapper">
      <div className="register-club-container-dark">
        <div className="register-club-logo">
          <img src="/logo.png" alt="Punt Club Logo" />
        </div>

        <h2 className="register-club-title">Start Your Club</h2>

        <p className="register-club-subtitle">
          Welcome, {user.firstname}!
        </p>

        {/* Progress Indicator */}
        <div className="progress-steps">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`progress-step ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
            >
              <div className="step-number">{index + 1}</div>
              <div className="step-info">
                <div className="step-title">{step.title}</div>
                <div className="step-description">{step.description}</div>
              </div>
            </div>
          ))}
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {success && (
          <div className="success-message">
            {success}
          </div>
        )}

        <form onSubmit={handleNext} className="register-club-form">
          {/* Step 0 - Club Details */}
          {currentStep === 0 && (
            <>
              <div className="step-header">
                <h3>Name your syndicate</h3>
                <p className="step-description-text">
                  Give your club a name that your mates will recognise
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="name">Club Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  autoFocus
                  placeholder="e.g., Saturday Punt Club, The Thoroughbreds"
                  disabled={loading}
                />
              </div>
            </>
          )}

          {/* Step 1 - Betting Settings */}
          {currentStep === 1 && (
            <>
              <div className="step-header">
                <h3>How will you bet?</h3>
                <p className="step-description-text">
                  Configure your club's betting strategy
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="betValue">Bet Amount</label>
                <div className="input-group">
                  <input
                    type="number"
                    id="betValue"
                    name="betValue"
                    value={formData.betValue}
                    onChange={handleChange}
                    min="1"
                    max="100"
                    required
                    disabled={loading}
                  />
                  <select
                    name="betType"
                    value={formData.betType}
                    onChange={handleChange}
                    className="input-addon"
                    disabled={loading}
                  >
                    <option value="PERCENT">% of Bank</option>
                    <option value="DOLLARS">$ Fixed</option>
                  </select>
                </div>
                <small className="field-hint">
                  {formData.betType === 'PERCENT'
                    ? 'Percentage of total club bank to bet'
                    : 'Fixed dollar amount per bet'}
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="betFrequency">How often do you bet?</label>
                <select
                  id="betFrequency"
                  name="betFrequency"
                  value={formData.betFrequency}
                  onChange={handleChange}
                  required
                  disabled={loading}
                >
                  <option value="weekly">Weekly</option>
                  <option value="daily">Daily</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="puntersPerPeriod">Punters per period</label>
                <select
                  id="puntersPerPeriod"
                  name="puntersPerPeriod"
                  value={formData.puntersPerPeriod}
                  onChange={handleChange}
                  required
                  disabled={loading}
                >
                  <option value="1">1 member bets at a time</option>
                  <option value="2">2 members bet at a time</option>
                  <option value="3">3 members bet at a time</option>
                  <option value="all">All members vote on bets</option>
                </select>
                <small className="field-hint">
                  How many members select bets each period
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="rolloverDay">Rollover Day</label>
                <select
                  id="rolloverDay"
                  name="rolloverDay"
                  value={formData.rolloverDay}
                  onChange={handleChange}
                  disabled={loading}
                >
                  {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
                <small className="field-hint">
                  The day when the betting period rolls over to the next punter
                </small>
              </div>
            </>
          )}

          {/* Step 2 - Member Contributions */}
          {currentStep === 2 && (
            <>
              <div className="step-header">
                <h3>Member Contributions</h3>
                <p className="step-description-text">
                  How much do members chip in to build the bank?
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="contributionAmount">Contribution Amount ($)</label>
                <input
                  type="number"
                  id="contributionAmount"
                  name="contributionAmount"
                  value={formData.contributionAmount}
                  onChange={handleChange}
                  min="5"
                  max="1000"
                  required
                  disabled={loading}
                />
                <small className="field-hint">
                  How much each member contributes
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="contributionFrequency">How often?</label>
                <select
                  id="contributionFrequency"
                  name="contributionFrequency"
                  value={formData.contributionFrequency}
                  onChange={handleChange}
                  required
                  disabled={loading}
                >
                  <option value="once">Once-off</option>
                  <option value="weekly">Weekly</option>
                  <option value="fortnightly">Fortnightly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>

              <div className="contribution-summary">
                <strong>Summary:</strong> Members contribute ${formData.contributionAmount} {formData.contributionFrequency === 'once' ? 'once' : formData.contributionFrequency}
              </div>
            </>
          )}

          {/* Step 3 - Connect Bookie */}
          {currentStep === 3 && (
            <>
              <div className="step-header">
                <h3>Connect Your AnyBookie Account</h3>
                <p className="step-description-text bookie-tagline">
                  A club with no bookie is like a pub with no beer.
                </p>
                <p className="step-description-text">
                  Choose from the options below to link your AnyBookie account.
                </p>
              </div>

              {/* Name Verification Warning */}
              {user && (
                <div className="info-box info-box-warning">
                  <p>
                    If <strong>{user.firstname} {user.surname}</strong> is not your legal full name,
                    then update it <a href="/profile">here</a> before you progress.
                  </p>
                </div>
              )}

              {/* Account Options */}
              <div className="account-options">
                <div
                  className={`account-option ${formData.accountAction === 'new' ? 'selected' : ''}`}
                  onClick={() => setFormData({...formData, accountAction: 'new'})}
                >
                  <div className="account-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <line x1="19" y1="8" x2="19" y2="14"></line>
                      <line x1="22" y1="11" x2="16" y2="11"></line>
                    </svg>
                  </div>
                  <div className="account-option-text">
                    <h4>I need an AnyBookie account</h4>
                    <p>Create a new betting account</p>
                  </div>
                </div>

                <div
                  className={`account-option ${formData.accountAction === 'existing' ? 'selected' : ''}`}
                  onClick={() => setFormData({...formData, accountAction: 'existing'})}
                >
                  <div className="account-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                  </div>
                  <div className="account-option-text">
                    <h4>I already have an AnyBookie account</h4>
                    <p>Link your existing betting account</p>
                  </div>
                </div>
              </div>

              <div className="info-box">
                <p>Punt Club uses secure authentication to access your bets. We never see your login credentials or personal betting details.</p>
              </div>
            </>
          )}

          {/* Navigation Buttons */}
          <div className="form-actions">
            {currentStep > 0 && (
              <button
                type="button"
                className="btn-secondary"
                onClick={handleBack}
                disabled={loading}
              >
                Back
              </button>
            )}

            <button
              type="submit"
              className="btn-warning"
              disabled={loading}
            >
              {loading && <span className="btn-spinner"></span>}
              {currentStep === steps.length - 1
                ? (loading ? 'Creating Club...' : 'Create Club')
                : 'Next'}
            </button>
          </div>

          <div className="skip-link">
            <a href="#" onClick={(e) => { e.preventDefault(); handleSkip(); }}>
              Skip for now - I'll set this up later
            </a>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RegisterClub;
